// src/features/records/stores/entityCache.js
import { create } from 'zustand';

/**
 * Entity LRU+TTL Cache Store
 *
 * Implements the Edge-Indexed architecture's bounded entity cache.
 * Entities are normalized and cached on-demand with automatic eviction.
 *
 * Architecture:
 * - Max 500 entities (configurable)
 * - 15-minute TTL with lazy expiration
 * - LRU eviction when over capacity
 * - O(1) lookups via Map structure
 *
 * See: /docs/implementation/2025-09-08-edge-index-implementation-guide.md
 */
const useEntityCache = create((set, get) => ({
  byId: new Map(),
  max: 500,
  ttlMs: 15 * 60 * 1000, // 15 minutes

  /**
   * Get a single entity by ID
   * Touches the entity to update LRU and checks TTL
   * @param {string} id - Entity ID
   * @returns {Object|undefined} Entity if found and not expired
   */
  get: (id) => {
    const e = get().byId.get(id);
    if (!e) return undefined;

    const now = Date.now();
    if (e.expiresAt < now) {
      // Expired; lazy evict
      const byId = new Map(get().byId);
      byId.delete(id);
      set({ byId });
      return undefined;
    }

    // Touch for LRU
    const byId = new Map(get().byId);
    byId.set(id, { ...e, touchedAt: now, expiresAt: now + get().ttlMs });
    set({ byId });
    return e.entity;
  },

  /**
   * Upsert multiple entities
   * @param {Array} entities - Array of entities to cache
   */
  upsertMany: (entities) => {
    const now = Date.now();
    const ttl = get().ttlMs;
    const byId = new Map(get().byId);

    for (const ent of entities) {
      byId.set(ent.id, {
        entity: ent,
        touchedAt: now,
        expiresAt: now + ttl
      });
    }

    // Prune LRU if over capacity
    while (byId.size > get().max) {
      // Evict least recently touched
      const oldest = [...byId.entries()].reduce((a, b) =>
        (a[1].touchedAt < b[1].touchedAt ? a : b)
      );
      byId.delete(oldest[0]);
    }

    set({ byId });
  },

  /**
   * Get multiple entities, fetching missing ones
   * @param {Array<string>} ids - Entity IDs to retrieve
   * @param {Function} fetcher - Async function to fetch missing entities
   * @returns {Promise<Array>} Array of entities
   */
  getOrFetchMany: async (ids, fetcher) => {
    const miss = [];
    const hit = [];

    for (const id of ids) {
      const e = get().get(id);
      if (e) {
        hit.push(e);
      } else {
        miss.push(id);
      }
    }

    if (miss.length) {
      const fetched = await fetcher(miss);
      get().upsertMany(fetched);
      return [...hit, ...fetched];
    }

    return hit;
  },

  /**
   * Remove expired entries
   */
  prune: () => {
    const now = Date.now();
    const byId = new Map(
      [...get().byId.entries()].filter(([, e]) => e.expiresAt >= now)
    );
    set({ byId });
  },

  /**
   * Clear all cached entities
   */
  clear: () => set({ byId: new Map() }),

  /**
   * Get cache statistics
   * @returns {Object} Cache stats
   */
  getStats: () => {
    const byId = get().byId;
    const now = Date.now();
    const expired = [...byId.values()].filter(e => e.expiresAt < now).length;

    return {
      size: byId.size,
      expired,
      active: byId.size - expired,
      maxSize: get().max,
      ttlMs: get().ttlMs
    };
  }
}));

export default useEntityCache;